
import React from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Shield, Check, X, BadgeCheck, BadgeX } from "lucide-react";
import { getStatusBadge } from "./ClaimsUtils";

interface ClaimDetailsCardProps {
  claim: any;
  adminNotes: string;
  setAdminNotes: (s: string) => void;
  onApprove: () => void;
  onReject: () => void;
  onClose: () => void;
}

const ClaimDetailsCard: React.FC<ClaimDetailsCardProps> = ({
  claim,
  adminNotes,
  setAdminNotes,
  onApprove,
  onReject,
  onClose,
}) => (
  <Card className="col-span-1">
    <CardHeader className="bg-muted/30">
      <div className="flex items-center justify-between">
        <CardTitle>Claim Details</CardTitle>
        {getStatusBadge(claim.status)}
      </div>
      <CardDescription>
        Submitted on {claim.created_at ? new Date(claim.created_at).toLocaleDateString() : ""}
      </CardDescription>
    </CardHeader>
    <CardContent className="pt-6 space-y-4">
      <div>
        <h3 className="font-medium mb-1 flex items-center">
          <Shield className="h-4 w-4 mr-1" /> Nursing Home
        </h3>
        <p className="text-sm">{claim.nursing_home?.center_name}</p>
        <p className="text-xs text-muted-foreground">{claim.nursing_home?.county}</p>
      </div>

      <div>
        <h3 className="font-medium mb-1">Claimant Information</h3>
        <p className="text-sm">
          {(claim.user?.user_metadata?.first_name || "") + " " + (claim.user?.user_metadata?.last_name || "")}
        </p>
        <p className="text-xs text-muted-foreground">{claim.user?.email}</p>
      </div>

      <div>
        <h3 className="font-medium mb-1">Role & Contact</h3>
        <p className="text-sm">{claim.verification_documents?.role || "Not specified"}</p>
        <p className="text-xs text-muted-foreground">
          Phone: {claim.verification_documents?.contactPhone || "Not provided"}
        </p>
      </div>

      {claim.notes && (
        <div>
          <h3 className="font-medium mb-1">Claimant Notes</h3>
          <p className="text-sm bg-muted/20 p-2 rounded">{claim.notes}</p>
        </div>
      )}

      {claim.status === "pending" && (
        <div>
          <Label htmlFor="adminNotes">Admin Notes</Label>
          <Textarea
            id="adminNotes"
            placeholder="Add notes about this claim (optional)"
            className="mt-1"
            value={adminNotes}
            onChange={e => setAdminNotes(e.target.value)}
          />
        </div>
      )}
    </CardContent>
    <CardFooter className={`flex ${claim.status === "pending" ? "justify-between" : "justify-end"}`}>
      {claim.status === "pending" && (
        <>
          <Button
            variant="destructive"
            onClick={onReject}
          >
            <X className="h-4 w-4 mr-1" /> Reject
          </Button>
          <Button
            variant="default"
            onClick={onApprove}
          >
            <Check className="h-4 w-4 mr-1" /> Approve
          </Button>
        </>
      )}
      <Button
        variant="outline"
        onClick={onClose}
      >
        Close
      </Button>
    </CardFooter>
  </Card>
);

export default ClaimDetailsCard;
